import type { ChangeEvent, FormEvent } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Typography, useToast } from "@aviary";
import { isEmailValid } from "@shared/components/EmailInput";
import { gRecaptchaExecute } from "@shared/utils";
import {
  FormWrapper,
  PasswordInput,
  PractitionerSignUpEmailInput,
  isPasswordValid,
} from "@unauthenticated/shared/components";
import { useGlobalConfig } from "@unauthenticated/shared/context";
import { useAccountExistsCheck } from "@unauthenticated/shared/hooks";
import { l } from "@unauthenticated/wellevateAuthorize/practitioners/locales/i18n";

import type { WellevateSignupFormData } from "../../types";

import { useAuthWellevatePractitionerSignUpMutation } from "./AuthWellevatePractitionerSignUp.mutation";
import type { AuthWellevatePractitionerSignUpData } from "./AuthWellevatePractitionerSignUp.mutation";

import * as styles from "./WellevateRegistrationPage.styles";

interface Props {
  formData: WellevateSignupFormData;
}

interface Credentials {
  email: string;
  password: string;
}

const WellevateRegistrationPage = ({ formData }: Props) => {
  const { t } = useTranslation();
  const { makeToast } = useToast();
  const { emailRegex } = useGlobalConfig();

  const [emailDirty, setEmailDirty] = useState(false);
  const [passwordDirty, setPasswordDirty] = useState(false);

  const {
    email: pracEmail,
    dispensaryName: storeName,
    firstName,
    lastName,
    practitionerTypeId,
    wellevatePractitionerId,
  } = formData;

  const [credentials, seCredentials] = useState<Credentials>({
    email: pracEmail,
    password: "",
  });

  const { email, password } = credentials;

  const {
    isAccountExists,
    setIsAccountExists,
    handleCheckIfAccountExists,
    checkIfAccountExistsWithRecaptcha,
    isAccountCheckLoading,
  } = useAccountExistsCheck();

  const handleError = (errorMessage: string) => {
    makeToast("error", errorMessage);
  };

  const handleAuthOnCompleted = (data: AuthWellevatePractitionerSignUpData) => {
    const errorMessage = data?.auth?.wellevatePractitionerSignUp?.errors?.message;
    const redirectPath = data?.auth?.wellevatePractitionerSignUp?.redirectPath;

    if (errorMessage) {
      handleError(errorMessage);
    }

    if (redirectPath) {
      return window.location.assign(redirectPath);
    }

    return window.location.assign("/");
  };

  const [authWellevatePractitionerSignUp, { loading }] = useAuthWellevatePractitionerSignUpMutation(
    {
      onCompleted: data => handleAuthOnCompleted(data),
      onError: ({ message: errorMessage }) => handleError(errorMessage),
    }
  );

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;
    seCredentials(prevState => ({ ...prevState, [name]: value }));
  };

  const onEmailBlur = e => {
    setEmailDirty(true);

    if (e?.target.value) {
      handleChange(e);
      handleCheckIfAccountExists(e.target.value);
    }
  };

  const setEmail = (emailEntered: string) => {
    seCredentials(prevState => ({ ...prevState, emailEntered }));
  };

  const isFormValid = () => {
    const emailValid = isEmailValid(email, emailRegex) && !isAccountExists;
    const passwordValid = isPasswordValid(password);

    return passwordValid && emailValid;
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!isFormValid()) {
      return;
    }

    gRecaptchaExecute(gToken => {
      authWellevatePractitionerSignUp({
        variables: {
          input: {
            attributes: {
              email,
              password,
              firstName,
              lastName,
              storeName,
              practitionerTypeId,
              wellevatePractitionerId,
              pageVersion: "react",
              signUpPage: "wellevate_loyalist_practitioner_sign_up_page",
              optional: {
                source: "wellevate",
                redirectPath: "/",
              },
            },
            captchaToken: gToken,
          },
        },
      });
    });
  };

  return (
    <div css={styles.wrapper}>
      <Typography type="h2">{t(l.common.RegistrationPage.title)}</Typography>
      <Typography>{t(l.common.RegistrationPage.subTitle)}</Typography>
      <form role="form" onSubmit={handleSubmit}>
        <FormWrapper>
          <PractitionerSignUpEmailInput
            value={email}
            onChange={handleChange}
            setEmail={setEmail}
            isDirty={emailDirty}
            required
            isAccountExists={isAccountExists}
            setIsAccountExists={setIsAccountExists}
            handleCheckIfAccountExists={handleCheckIfAccountExists}
            checkIfAccountExistsWithRecaptcha={checkIfAccountExistsWithRecaptcha}
            wrapperStyles={styles.input}
            onBlur={onEmailBlur}
          />
          <PasswordInput
            value={password}
            onChange={handleChange}
            isLoading={false}
            isDirty={passwordDirty}
            onFocus={() => setPasswordDirty(true)}
            required
            wrapperStyles={styles.input}
          />
          <Button
            type="submit"
            isFullWidth
            disabled={!isFormValid()}
            isLoading={isAccountCheckLoading || loading}
          >
            {t(l.common.RegistrationPage.buttonLabel)}
          </Button>
        </FormWrapper>
      </form>
    </div>
  );
};

export { WellevateRegistrationPage };
